import type { ReactNode } from 'react';
import type { LibraryRow } from '@/state/experiments/library-learnings';
import { useState, useEffect, useRef } from 'react';
import { NavLink } from '@/components/NavLink';
import { TopHeader } from './TopHeader';
import { formatDateTime } from 'helpers/formatters';
import { DataTable, useDataControl } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { UserAvatar } from '@/components/UserAvatar';
import { ActionsCell } from './ActionsCell';
import { useExperimentsList } from '@/state/experiments/useExperimentsList';
import { planOpen, websiteSwitched } from '@/state/experiments/plan-open';
import { useLearningsStatus } from '@/state/learnings/useLearningsStatus';
import { useWebsites } from '@/state/useWebsites';
import { RotateCcw, Clock, X, Check, Eye, ExternalLink, FlaskConical, Plus, ChevronRight } from 'lucide-react';
import { cn } from 'helpers/cn';
import { DisclosureButton, FOCUS_RING } from '../../ConsolePanel';
import { CreateExperimentModal } from './CreateExperimentModal';
import { ScheduleBadge } from './ScheduleBadge';
import { GeneratingLearnings } from './GeneratingLearnings';
import { LearningsDetail, ResultChip, learningsPanelId } from './Learnings';
import { TimelineView } from './TimelineView';

/**
 * Searched on the prepared `searchText` (hypothesis, notes, mechanisms, variants,
 * outcome) as well as the name, so "delivery" finds the test whose hypothesis was about
 * delivery dates even though its name was "PDP badge v2".
 */
const SEARCH_KEYS: (keyof LibraryRow)[] = ['id', 'name', 'status', 'searchText'];

/** One number for the table and for the deep link that has to work out which page a row is on. */
const PAGE_SIZE = 10;

/** Views where tests have concluded, and so have a result worth a column. */
const RESULT_VIEWS = ['All', 'Ended', 'Deployed', 'Archived'];

/**
 * Console status semantics: Running is live (ok), Deployed is the shipped outcome
 * (accent), Draft is a selection still being made (sel), Ended is neither good nor bad
 * — a plain hairline chip rather than a colour implying something went wrong with it.
 */
type StatusChip = { icon: React.ComponentType<{ className?: string }>; chip: string };

const STATUS_CHIP: Record<string, StatusChip> = {
  Running: { icon: RotateCcw, chip: 'border-ok-line text-ok' },
  Deployed: { icon: Check, chip: 'border-accent-line text-accent' },
  Draft: { icon: Clock, chip: 'border-line text-dim' },
  // A draft being previewed on the live site: the selection tone, as drafts have on the timeline.
  QA: { icon: Eye, chip: 'border-sel-line text-sel' },
  Ended: { icon: X, chip: 'border-line text-dim' },
};

const CHIP_CLASS = 'micro-label rounded-pill inline-flex items-center gap-1.5 border px-2 py-1';

export const ExperimentsLibrary = () => {
  const manager = useExperimentsList();
  const { view, experiments, isLoading, isError, refetch, hasWebsite, totalCount } = manager;
  const { viewEmptyMessage, viewNoMatchMessage, setView } = manager;
  const learnings = useLearningsStatus();
  const { website } = useWebsites();
  // The timeline ignores the status views (see useExperimentsList), so it is fed its own
  // rows; the search still runs over them through the same controller.
  const dataController = useDataControl<LibraryRow>(
    manager.layout === 'timeline' ? manager.timelineRows : experiments || [],
  );

  // Back to page one when the filter or view narrows the set: the table only resets on
  // its own search and sort, so page 3 of 25 rows could land on an empty page 3 of 4.
  useEffect(() => {
    dataController.changePage(1);
  }, [manager.outcome, manager.mechanism, manager.changeType, manager.element, manager.theme, view]);

  /*
    ?open=<experimentId>, or a request made on this screen: bring that test's learnings into view,
    then drop the key. Whatever is hiding the row has to go first, one thing per pass (the timeline
    layout, a status view, a filter, either search), and each change re-runs this once the
    table has caught up. setView also clears the three learnings filters, which is why that step
    comes before the filter one. An id that is not on this website is dropped without a word: the
    link may be old, and a stuck key would re-run this on every change.

    The page is changed and then waited for. The table resets itself to page one when its search
    keys are first set and on a search change, so a page chosen in the same pass as one of those
    would be undone: the key is consumed only once the table is on the page the row is on, and
    until its search keys exist nothing is chosen at all.
  */
  const [scrollTo, setScrollTo] = useState<number | null>(null);

  useEffect(() => {
    const target = manager.openTarget;
    const step = planOpen({
      target,
      rows: manager.allRows,
      tableReady: !!dataController.searchKeys,
      layout: manager.layout,
      view,
      hasLearningFilter: manager.hasLearningFilter,
      nameSearch: manager.searchText,
      tableSearch: dataController.searchQuery,
      matching: dataController.matchingData,
      currentPage: dataController.currentPage,
      pageSize: dataController.pageSize,
    });

    switch (step.do) {
      case 'wait':
        return;
      case 'drop':
        return manager.consumeOpen();
      case 'show-table':
        return manager.setLayout('table');
      case 'show-view':
        return setView(step.view);
      case 'clear-filters':
        return manager.clearLearningFilters();
      case 'clear-name-search':
        return manager.handleSearch('');
      case 'clear-table-search':
        return dataController.setSearchQuery('');
      case 'go-to-page':
        return dataController.changePage(step.page);
      case 'open':
        if (target === null) return;
        manager.openRow(target);
        manager.consumeOpen();
        setScrollTo(target);
    }
  }, [
    manager.openTarget,
    manager.allRows,
    manager.layout,
    manager.hasLearningFilter,
    manager.searchText,
    view,
    dataController.searchKeys,
    dataController.searchQuery,
    dataController.matchingData,
    dataController.currentPage,
    dataController.pageSize,
  ]);

  /*
    A request belongs to the website it was made on. It lives in the query cache and the ?open= value
    in component state, and both outlast a website switch: left alone, a cited test on one website
    would open the row with that id on whichever website is picked next, unprompted. Cleared when the
    website changes. The ref tells a switch from the first resolve (previous is null) and from leaving
    the screen, which an effect cleanup could not.
  */
  const lastWebsite = useRef<string | number | null>(null);
  useEffect(() => {
    const previous = lastWebsite.current;
    lastWebsite.current = manager.websiteId;
    if (websiteSwitched({ previous, current: manager.websiteId })) manager.consumeOpen();
  }, [manager.websiteId]);

  // After the row has rendered open: the panel does not exist until the state change lands, so this
  // waits for it, and needs no timer to guess how long that takes.
  const scrollTargetIsOpen = scrollTo !== null && manager.isOpen(scrollTo);
  useEffect(() => {
    if (scrollTo === null || !scrollTargetIsOpen) return;
    const panel = document.getElementById(learningsPanelId(scrollTo));
    if (!panel) return;
    panel.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setScrollTo(null);
  }, [scrollTo, scrollTargetIsOpen, dataController.currentPage]);

  // Set here as well as on the table: the table sets its keys when it mounts, and a
  // Library opened straight into the timeline never mounts it, so search did nothing.
  useEffect(() => {
    dataController.setSearchKeys(SEARCH_KEYS);
  }, []);

  /*
    Five situations used to arrive at the table as one and render its default
    "No Data Found.": no website picked, the request in flight, a failed request, a
    website with genuinely no experiments, and a view pill that excluded every row.

    The query is gated on a website, and a disabled React Query reports isLoading false
    with no data, so the first three are invisible from the data alone — they are read
    off the flags, in that order, before anything is drawn. Only the fourth is an empty
    library and only it gets the explainer; the fifth is left to the table, which now
    has a sentence naming the view.
  */
  if (!hasWebsite) return <LibraryNotice>Select a website first.</LibraryNotice>;

  if (isLoading) return <LibraryNotice>Loading experiments…</LibraryNotice>;

  if (isError) {
    return (
      <LibraryNotice>
        Could not load the experiments for this website.
        {/* Wrapped: DisclosureButton carries `self-start`, which would pull it off the
            notice's centre line if it were a direct child of the centred column. */}
        <div>
          <DisclosureButton onClick={() => refetch()}>Try again</DisclosureButton>
        </div>
      </LibraryNotice>
    );
  }

  if (totalCount === 0) return <ExperimentsEmptyState />;

  return (
    <main className="flex flex-col gap-[14px]">
      <section className="bg-panel border-line rounded-panel border">
        {/* While the cron works through a website's concluded tests: how far it has got, or why
            it has stopped when the account's AI credits have paused it. */}
        {learnings.isGenerating && (
          <div className="border-line flex border-b px-4 py-2">
            <GeneratingLearnings
              labelled={learnings.labelled}
              concluded={learnings.concluded}
              paused={learnings.paused}
            />
          </div>
        )}
        <TopHeader manager={manager} dataController={dataController} />

        <div className="p-3">
          {manager.layout === 'timeline' ? (
            <TimelineView allRows={manager.allRows} visibleRows={dataController.matchingData ?? []} />
          ) : (
            <DataTable<LibraryRow>
              dataController={dataController}
              searchKeys={SEARCH_KEYS}
              renderExpanded={(item) => (manager.isOpen(item.id) ? <LearningsDetail row={item} /> : null)}
              pageSize={PAGE_SIZE}
              noDataMessage={viewEmptyMessage}
              noMatchMessage={viewNoMatchMessage}
              /* Only reachable with the set already empty, which after the guards above
               means the view did it — so the way out is the view, not a create button. */
              noDataCTA={
                manager.hasLearningFilter ? (
                  <div className="mt-2">
                    <DisclosureButton onClick={manager.clearLearningFilters}>Clear filters</DisclosureButton>
                  </div>
                ) : view !== 'All' ? (
                  <div className="mt-2">
                    <DisclosureButton onClick={() => setView('All')}>Show all experiments</DisclosureButton>
                  </div>
                ) : undefined
              }
              columns={[
                {
                  // First, so the control sits where the eye starts the row; a card puts
                  // it in the footer with the other actions.
                  // Named for screen readers, invisible on screen: an empty header cell
                  // left the column unlabelled.
                  label: 'Details',
                  headerClassName: 'w-8 text-transparent select-none',
                  cellClassName: 'w-8 pr-0',
                  mobile: 'action',
                  format: ({ item, idx }) => {
                    const open = manager.isOpen(item.id);
                    return (
                      <button
                        type="button"
                        aria-expanded={open}
                        aria-controls={open ? learningsPanelId(item.id) : undefined}
                        aria-label={open ? `Hide learnings for ${item.name}` : `Show learnings for ${item.name}`}
                        onClick={() => manager.toggleOpen(item.id)}
                        // The first row carries the product tour's anchor.
                        data-tour={idx === 0 ? 'experiments-learnings' : undefined}
                        className={cn(
                          'text-dim hover:text-foreground hover:bg-hover flex size-7 items-center justify-center rounded-md',
                          FOCUS_RING,
                        )}
                      >
                        <ChevronRight className={cn('size-4 transition-transform duration-150', open && 'rotate-90')} />
                      </button>
                    );
                  },
                },
                {
                  field: 'id',
                  label: 'ID',
                  sortKey: 'id',
                  headerClassName: 'micro-label w-20',
                  cellClassName: 'num text-dim',
                  mobile: 'meta',
                },
                {
                  field: 'name',
                  label: 'Experiment',
                  sortKey: 'name',
                  headerClassName: 'micro-label',
                  cellClassName: 'min-w-0',
                  mobile: 'title',
                  format: ({ item }) => (
                    // hover:underline on the link, not group-hover on the row: the row has
                    // no onClick, so the row-wide underline advertised a link that is only
                    // on the name cell.
                    <NavLink
                      to={`/experiments/${item.id}`}
                      title={item.name}
                      className="text-foreground flex min-w-0 items-center gap-2 font-medium hover:underline"
                    >
                      <span className="truncate">{item.name}</span>
                      <ExternalLink className="text-dim h-3.5 w-3.5 flex-shrink-0" />
                    </NavLink>
                  ),
                },
                {
                  field: 'createdAt',
                  label: 'Created',
                  sortKey: 'createdAt',
                  condition: ['Draft', 'QA'].includes(view),
                  headerClassName: 'micro-label w-32',
                  cellClassName: 'num text-dim',
                  // Only visible on the Draft and QA views, where it is the row's only date.
                  mobile: 'meta',
                  format: ({ value }) => formatDateTime(value as number) || '-',
                },
                {
                  field: 'startsAt',
                  label: 'Start date',
                  labelMobile: 'Started',
                  sortKey: 'startsAt',
                  condition: !['Draft', 'QA'].includes(view),
                  headerClassName: 'micro-label w-32',
                  cellClassName: 'num text-dim',
                  mobile: 'meta',
                  format: ({ value }) => formatDateTime(value as number) || '-',
                },
                {
                  field: 'endsAt',
                  label: 'End date',
                  sortKey: 'endsAt',
                  condition: !['Draft', 'QA', 'Running'].includes(view),
                  headerClassName: 'micro-label w-32',
                  cellClassName: 'num text-dim',
                  // Two dates is one too many for a card; the start date is the one that
                  // places an experiment. Still offered as a sort.
                  mobile: 'hide',
                  format: ({ value }) => formatDateTime(value as number) || '-',
                },
                {
                  field: 'createdByUser',
                  label: 'Created by',
                  sortKey: 'createdByUser',
                  headerClassName: 'micro-label w-20',
                  mobile: 'hide',
                  // The badge stands in for the name, which a 112px column used to cut
                  // off anyway; hovering it gives the full name.
                  format: ({ item }) =>
                    item.createdByUser ? <UserAvatar user={item.createdByUser} size="sm" tooltip /> : '-',
                },
                {
                  field: 'resultSort',
                  label: 'Result',
                  sortKey: 'resultSort',
                  condition: RESULT_VIEWS.includes(view),
                  headerClassName: 'micro-label w-32',
                  mobile: 'meta',
                  format: ({ item, idx }) => (
                    // The first row carries the product tour's anchor.
                    <span data-tour={idx === 0 ? 'experiments-result' : undefined}>
                      <ResultChip reading={item.reading} />
                    </span>
                  ),
                },
                {
                  field: 'status',
                  label: 'Status',
                  sortKey: 'status',
                  condition: ['Archived', 'All'].includes(view),
                  headerClassName: 'micro-label w-28',
                  mobile: 'meta',
                  format: ({ item, idx }) => {
                    const { status } = item;
                    const descriptor = STATUS_CHIP[status];
                    const Icon = descriptor?.icon;
                    return (
                      <div
                        className="flex flex-col items-start gap-1"
                        data-tour={idx === 0 ? 'experiments-status' : undefined}
                      >
                        <span className={cn(CHIP_CLASS, descriptor?.chip ?? 'border-line text-dim')}>
                          {Icon && <Icon className="h-3 w-3" />}
                          {status.charAt(0).toUpperCase() + status.slice(1)}
                        </span>
                        <ScheduleBadge experiment={item} />
                      </div>
                    );
                  },
                },
                {
                  label: 'Actions',
                  headerClassName: 'micro-label w-24',
                  cellClassName: 'w-24',
                  mobile: 'action',
                  format: ({ item, idx }) => (
                    <ActionsCell experiment={item} tourAnchor={idx === 0 ? 'experiments-actions' : undefined} />
                  ),
                },
              ]}
            />
          )}
        </div>
      </section>

      {/* Asking the tests and the open questions they raise live on the Learnings page now. */}
      {website?.learningsEnabled && manager.layout === 'table' && (
        <p className="supporting m-0">
          Ask your tests a question, and see the patterns and open questions they raise, on{' '}
          <NavLink to="/experiments/learnings" className="text-accent hover:underline">
            Learnings
          </NavLink>
          .
        </p>
      )}
    </main>
  );
};

type LibraryNoticeProps = { children: ReactNode };

/**
 * The one-line answer for a library that cannot be drawn yet.
 *
 * A panel rather than bare text, and the same panel the table would have occupied, so
 * the page does not change shape between "loading" and "here are your experiments".
 */
const LibraryNotice = ({ children }: LibraryNoticeProps) => (
  <main className="bg-panel border-line rounded-panel border px-6 py-12">
    <div className="supporting flex flex-col items-center gap-2 text-center">{children}</div>
  </main>
);

/** The life of an experiment, in the order someone will actually walk it. */
const STEPS = [
  'Create a draft: what kind of experiment it is, and what the variants are called. Nothing is live yet.',
  'Build the variants — visually in the editor, or by pointing each one at its own address.',
  'Start it. Traffic splits between the variants, and the numbers build up under Monitors.',
  'When one wins, deploy it: every visitor gets that version and the split stops.',
];

/**
 * First-run screen for the experiments library.
 *
 * Same standard as SurveysEmptyState and PipelinesEmptyState: the table's default
 * "No Data Found." left a first-time reader to work out what an experiment is from six
 * column headings. It replaces the toolbar rather than sitting under it, because with
 * nothing to filter, search or sort those controls all do nothing — so it carries the
 * one control that ends this state, which is also why it holds the create dialog
 * itself rather than sending the reader back to a toolbar that is not on screen.
 */
const ExperimentsEmptyState = () => {
  const [isCreating, setIsCreating] = useState<boolean>(false);

  return (
    <main className="flex flex-col gap-[14px]">
      <section className="bg-panel border-line rounded-panel border px-4 py-10 sm:px-6 sm:py-12" data-first-run>
        <div className="mx-auto max-w-2xl text-center">
          <FlaskConical className="text-dim mx-auto size-10" strokeWidth={1.25} />
          {/* h1: the empty state replaces the whole library, so this is the page's only
              heading — same level as the surveys and pipelines explainers. */}
          <h1 className="text-foreground m-0 mt-4 text-title font-medium tracking-tight">No experiments yet</h1>
          <p className="supporting mx-auto mt-2 max-w-xl">
            An experiment shows different versions of your site to different visitors and records what each group does.
            The point is the comparison: a change ships because it measurably beat what was there before, rather than
            because it was the most confidently argued for.
          </p>

          <Button onClick={() => setIsCreating(true)} className="mt-5 h-9 rounded-lg">
            <Plus className="size-4" />
            Create experiment
          </Button>
        </div>
      </section>

      <section className="bg-panel border-line rounded-panel border">
        <div className="border-line border-b px-3 py-2.5">
          <span className="micro-label">What happens next</span>
        </div>
        <ol className="m-0 flex list-none flex-col gap-2.5 p-3">
          {STEPS.map((step, index) => (
            <li key={step} className="flex items-start gap-2.5">
              <span
                className={cn(
                  'border-line text-dim num flex size-5 shrink-0 items-center justify-center',
                  'rounded-full border text-nano',
                )}
                aria-hidden="true"
              >
                {index + 1}
              </span>
              <p className="supporting m-0">{step}</p>
            </li>
          ))}
        </ol>
        <div className="border-line border-t px-3 py-2.5">
          {/* The fact most likely to cost someone a day, and the same one the surveys
              explainer states: creating is not starting, and starting is not publishing. */}
          <p className="supporting m-0">
            A new experiment is a draft. It only reaches visitors once you start it and the website is published.
          </p>
        </div>
      </section>

      {isCreating && <CreateExperimentModal isCreating={isCreating} setIsCreating={setIsCreating} />}
    </main>
  );
};
